import { useState, useEffect, useRef } from "react";
import { MessageSquare, Send, Loader2, X, Search } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";

interface Props {
  restaurants: any[];
  currentUserId: string | null;
}

const notificationSound = new Audio("data:audio/wav;base64,UklGRnoGAABXQVZFZm10IBAAAAABAAEAQB8AAEAfAAABAAgAZGF0YQoGAACBhYqFbF1fdJivrJBhNjVgip2teleglnlZXICisp1xRT1pmLGdfU9AaJmtnXlRO2eXrJ55UD1nlaucc0s6Y5OsnHdNO2OTrJx3TTtjk6yce007Y5OsnHdNO2OTrJx3TTtj");

const AdminSupportTab = ({ restaurants, currentUserId }: Props) => {
  const { toast } = useToast();
  const [conversations, setConversations] = useState<any[]>([]);
  const [messages, setMessages] = useState<any[]>([]);
  const [selectedConv, setSelectedConv] = useState<string | null>(null);
  const [newMessage, setNewMessage] = useState("");
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [search, setSearch] = useState("");
  const [unreadCount, setUnreadCount] = useState(0);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const getRestaurantName = (restaurantId: string) => {
    return restaurants.find(r => r.id === restaurantId)?.name || "Restaurante";
  };

  // Request notification permission on mount
  useEffect(() => {
    if ("Notification" in window && Notification.permission === "default") {
      Notification.requestPermission();
    }
  }, []);

  useEffect(() => {
    loadConversations();

    const channel = supabase
      .channel("admin-support")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "support_messages" }, (payload) => {
        const msg = payload.new as any;
        setMessages(prev => [...prev, msg]);
        setConversations(prev => prev.map(c =>
          c.id === msg.conversation_id ? { ...c, updated_at: msg.created_at } : c
        ));

        if (msg.sender_type === "restaurant") {
          setUnreadCount(prev => prev + 1);
          notificationSound.play().catch(() => {});

          toast({
            title: "💬 Nova mensagem de suporte",
            description: msg.content.length > 60 ? msg.content.slice(0, 60) + "..." : msg.content,
          });

          if ("Notification" in window && Notification.permission === "granted") {
            new Notification("Nova mensagem de suporte", {
              body: msg.content.length > 100 ? msg.content.slice(0, 100) + "..." : msg.content,
              icon: "/favicon.ico",
            });
          }
        }
      })
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "support_conversations" }, (payload) => {
        const conv = payload.new as any;
        setConversations(prev => [conv, ...prev]);
        setUnreadCount(prev => prev + 1);

        const restName = getRestaurantName(conv.restaurant_id);
        toast({
          title: "🆕 Nova conversa de suporte",
          description: `${restName} iniciou uma conversa: ${conv.subject}`,
        });

        if ("Notification" in window && Notification.permission === "granted") {
          new Notification(`Nova conversa — ${restName}`, {
            body: conv.subject,
            icon: "/favicon.ico",
          });
        }
      })
      .subscribe();

    return () => { supabase.removeChannel(channel); };
  }, [restaurants]);

  useEffect(() => {
    if (selectedConv) loadMessages(selectedConv);
  }, [selectedConv]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const loadConversations = async () => {
    const { data } = await supabase.from("support_conversations")
      .select("*").order("updated_at", { ascending: false });
    setConversations(data || []);
    setLoading(false);
  };

  const loadMessages = async (convId: string) => {
    const { data } = await supabase.from("support_messages")
      .select("*").eq("conversation_id", convId).order("created_at", { ascending: true });
    setMessages(data || []);
  };

  const sendMessage = async () => {
    if (!newMessage.trim() || !selectedConv || !currentUserId) return;
    setSending(true);
    const { error } = await supabase.from("support_messages").insert({
      conversation_id: selectedConv,
      sender_id: currentUserId,
      sender_type: "admin",
      content: newMessage.trim(),
    });
    if (error) toast({ title: "Erro ao enviar", variant: "destructive" });
    else setNewMessage("");
    setSending(false);
  };

  const closeConversation = async (convId: string) => {
    await supabase.from("support_conversations").update({ status: "closed" }).eq("id", convId);
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, status: "closed" } : c));
    toast({ title: "Conversa encerrada" });
  };

  const reopenConversation = async (convId: string) => {
    await supabase.from("support_conversations").update({ status: "open" }).eq("id", convId);
    setConversations(prev => prev.map(c => c.id === convId ? { ...c, status: "open" } : c));
  };


  const filteredConvs = conversations.filter(c => {
    const name = getRestaurantName(c.restaurant_id).toLowerCase();
    return name.includes(search.toLowerCase()) || c.subject.toLowerCase().includes(search.toLowerCase());
  });

  if (loading) return <div className="flex justify-center p-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display font-semibold text-lg">Chat de Suporte</h2>
          <p className="text-xs text-muted-foreground">{conversations.filter(c => c.status === "open").length} conversas abertas</p>
        </div>
        {unreadCount > 0 && (
          <Badge className="bg-destructive text-destructive-foreground animate-pulse">
            {unreadCount} nova{unreadCount > 1 ? "s" : ""}
          </Badge>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 h-[600px]">
        {/* Conversations list */}
        <div className="rounded-xl border border-border bg-card flex flex-col overflow-hidden">
          <div className="p-3 border-b border-border">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar..." className="pl-10 h-9 text-sm bg-secondary/50" />
            </div>
          </div>
          <div className="flex-1 overflow-y-auto">
            {filteredConvs.length === 0 ? (
              <div className="p-8 text-center text-muted-foreground text-sm">
                <MessageSquare className="h-6 w-6 mx-auto mb-2 opacity-30" />
                <p>Nenhuma conversa</p>
              </div>
            ) : (
              filteredConvs.map(conv => (
                <button
                  key={conv.id}
                  onClick={() => setSelectedConv(conv.id)}
                  className={`w-full text-left p-3 border-b border-border/50 hover:bg-secondary/50 transition-colors ${selectedConv === conv.id ? "bg-secondary" : ""}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{getRestaurantName(conv.restaurant_id)}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{conv.subject}</p>
                    </div>
                    <Badge variant={conv.status === "open" ? "default" : "secondary"} className="text-[9px] shrink-0">
                      {conv.status === "open" ? "Aberto" : "Fechado"}
                    </Badge>
                  </div>
                  <p className="text-[10px] text-muted-foreground mt-1">
                    {new Date(conv.updated_at).toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}
                  </p>
                </button>
              ))
            )}
          </div>
        </div>

        {/* Chat area */}
        <div className="lg:col-span-2 rounded-xl border border-border bg-card flex flex-col overflow-hidden">
          {selectedConv ? (
            <>
              <div className="p-3 border-b border-border flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium">
                    {getRestaurantName(conversations.find(c => c.id === selectedConv)?.restaurant_id || "")}
                  </p>
                  <p className="text-[10px] text-muted-foreground">
                    {conversations.find(c => c.id === selectedConv)?.subject}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {conversations.find(c => c.id === selectedConv)?.status === "open" ? (
                    <Button size="sm" variant="outline" onClick={() => closeConversation(selectedConv)} className="text-xs h-8">
                      <X className="h-3 w-3 mr-1" />Encerrar
                    </Button>
                  ) : (
                    <Button size="sm" variant="outline" onClick={() => reopenConversation(selectedConv)} className="text-xs h-8">
                      Reabrir
                    </Button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-4 space-y-3">
                {messages.filter(m => m.conversation_id === selectedConv).map(msg => (
                  <div key={msg.id} className={`flex ${msg.sender_type === "admin" ? "justify-end" : "justify-start"}`}>
                    <div className={`max-w-[75%] rounded-xl px-4 py-2.5 ${
                      msg.sender_type === "admin"
                        ? "bg-primary text-primary-foreground"
                        : "bg-secondary text-foreground"
                    }`}>
                      <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                      <p className={`text-[9px] mt-1 ${msg.sender_type === "admin" ? "text-primary-foreground/60" : "text-muted-foreground"}`}>
                        {msg.sender_type === "admin" ? "Suporte" : "Lojista"} · {new Date(msg.created_at).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" })}
                      </p>
                    </div>
                  </div>
                ))}
                <div ref={messagesEndRef} />
              </div>

              {conversations.find(c => c.id === selectedConv)?.status === "open" && (
                <div className="p-3 border-t border-border">
                  <div className="flex gap-2">
                    <Input
                      value={newMessage}
                      onChange={e => setNewMessage(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && !e.shiftKey && (e.preventDefault(), sendMessage())}
                      placeholder="Digite sua resposta..."
                      className="h-10 text-sm"
                    />
                    <Button onClick={sendMessage} disabled={sending || !newMessage.trim()} size="sm" className="h-10 px-4 bg-gradient-primary text-primary-foreground">
                      {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                    </Button>
                  </div>
                </div>
              )}
            </>
          ) : (
            <div className="flex-1 flex items-center justify-center text-muted-foreground">
              <div className="text-center">
                <MessageSquare className="h-10 w-10 mx-auto mb-3 opacity-20" />
                <p className="text-sm">Selecione uma conversa</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminSupportTab;
